import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackProductEvent } from "@/utils/analytics";
import {useAuth } from "@/features/auth";

const SESSION_KEY = "__analytics_page_stats";

const readSessionStats = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeSessionStats = (stats) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(stats));
  } catch {}
};

export const FEATURE_MAP = {
  "/dashboard": "educator_dashboard",
  "/studentdashboard": "student_dashboard",
  "/studentsupports": "student_supports",
  "/resourcetab": "resources",
  "/wellnessadministrator": "wellness_admin",
  "/wellnessstudents": "wellness_students",
  "/studentsscreen": "students_list",
  "/classscreen": "classes_list",
  "/schoolsscreen": "schools_list",
  "/optionsscreen": "settings",
  "/performaction": "perform_action",
  "/howdoifeel": "emotion_checkin",
  "/actions": "actions",
};

export function useAnalytics() {
  const {user} = useAuth();
  const location = useLocation();

  const pageStateRef = useRef({
    path: location.pathname,
    start: performance.now(),
  });
  const firstFeatureTrackedRef = useRef(false);
  const sessionSummarySentRef = useRef(false);

  const trackPageEntry = (path) => {
    const stats = readSessionStats();
    const count = Number(stats.total_pages ?? 0);

    if (!stats.session_start_ts) {
      stats.session_start_ts = Date.now();

      try {
        sessionStorage.removeItem("__analytics_first_action");
      } catch {}
    }

    if (!stats.first_page) {
      stats.first_page = path;
      trackProductEvent(
        "session_first_page",
        { page: path, total_pages: count + 1 },
        user,
      );
    } else if (!stats.second_page) {
      stats.second_page = path;
      trackProductEvent(
        "session_second_page",
        { page: path, total_pages: count + 1 },
        user,
      );
    }

    writeSessionStats({ ...stats, total_pages: count + 1, last_page: path });
  };

  const trackPageExit = (path, durationMs, reason) => {
    const stats = readSessionStats();

    const longest =
      !stats.longest_duration_ms || durationMs > stats.longest_duration_ms
        ? { longest_duration_ms: durationMs, longest_page: path }
        : {};
    const shortest =
      !stats.shortest_duration_ms || durationMs < stats.shortest_duration_ms
        ? { shortest_duration_ms: durationMs, shortest_page: path }
        : {};
    writeSessionStats({ ...stats, ...longest, ...shortest, last_page: path });
    trackProductEvent(
      "page_exit",
      { page: path, duration_ms: durationMs, reason },
      user,
    );
  };

  const sendSessionSummary = (reason) => {
    if (sessionSummarySentRef.current) {
      return;
    }
    sessionSummarySentRef.current = true;

    const current = pageStateRef.current;

    if (current?.path) {
      const durationMs = Math.max(
        0,
        Math.round(performance.now() - current.start),
      );
      trackPageExit(current.path, durationMs, reason);
    }

    const stats = readSessionStats();
    const sessionDurationMs = stats.session_start_ts
      ? Math.max(0, Date.now() - stats.session_start_ts)
      : undefined;

    trackProductEvent(
      "session_pages_summary",
      {
        first_page: stats.first_page,
        second_page: stats.second_page,
        last_page: stats.last_page,
        total_pages: stats.total_pages ?? 0,
        longest_page: stats.longest_page,
        longest_duration_ms: stats.longest_duration_ms,
        shortest_page: stats.shortest_page,
        shortest_duration_ms: stats.shortest_duration_ms,
        session_duration_ms: sessionDurationMs,
      },
      user,
    );

    if (sessionDurationMs !== undefined) {
      trackProductEvent(
        "session_duration",
        {
          duration_ms: sessionDurationMs,
          total_pages: stats.total_pages ?? 0,
        },
        user,
      );
    }
  };

  useEffect(() => {
    if (!user?._id) {
      return;
    }

    const { pathname } = location;

    trackProductEvent("page_view", { path: pathname, role: user.role }, user);

    trackPageEntry(pathname);

    const previous = pageStateRef.current;
    if (previous?.path && previous.path !== pathname) {
      const durationMs = Math.max(
        0,
        Math.round(performance.now() - previous.start),
      );
      trackPageExit(previous.path, durationMs, "navigation");
    }
    pageStateRef.current = { path: pathname, start: performance.now() };

    if (window.__loginStart && !window.__loginTimingTracked) {
      trackProductEvent(
        "data_load_time",
        {
          stage: "sign_in_to_home",
          duration_ms: Math.max(
            0,
            Math.round(performance.now() - window.__loginStart),
          ),
          page: pathname,
          role: user.role,
        },
        user,
      );

      window.__loginTimingTracked = true;
    }

    const feature = FEATURE_MAP[pathname];
    if (feature) {
      trackProductEvent(
        "feature_used",
        { feature, page: pathname, role: user.role },
        user,
      );

      if (!firstFeatureTrackedRef.current) {
        firstFeatureTrackedRef.current = true;
        trackProductEvent(
          "funnel_step",
          {
            funnel: "onboarding",
            step: "first_feature_used",
            page: pathname,
            role: user.role,
          },
          user,
        );
      }
    }
  }, [location.pathname, user?._id]);

  useEffect(() => {
    if (!user?._id) {
      return;
    }

    const onHidden = () => {
      if (document.visibilityState === "hidden") {
        sendSessionSummary("exit");
      }
    };

    const onUnload = () => sendSessionSummary("exit");

    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("beforeunload", onUnload);

    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("beforeunload", onUnload);
      sendSessionSummary("unmount");
    };
  }, [user._id]);
}
